import { Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import LoginPage from "./pages/LoginPage";
import "./App.css";
import PasswordReset from "./components/PasswordReset";
import SignupPage from "./pages/SignupPage";
import Loader from "./components/Loader";
import SentryErrorBoundary from "./components/SentryErrorBoundry";
import HomePage from "./pages/HomePage";
import { useNavigate } from "react-router-dom";
import { storage as localStorage } from "./utils/storage";
import { KeepAwake } from "@capacitor-community/keep-awake";

const ProtectedRoute = ({
  element,
  isAuthenticated,
}: {
  element: React.ReactElement;
  isAuthenticated: boolean;
}) => {
  return isAuthenticated ? element : <Navigate to="/" replace />;
};

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Refresh access token every 50 minutes
    const getAccessToken = async () => {
      const refreshToken = await localStorage.get("refreshToken");
      if (refreshToken) {
        try {
          setIsLoading(true);
          const response = await fetch(
            "https://refreshtoken-madox6irya-uc.a.run.app",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ refreshToken }),
            }
          );

          const data = await response.json();
          setIsLoading(false);

          if (data.accessToken) {
            await localStorage.set("accessToken", data.accessToken);
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error("Error refreshing token:", error);
          setIsLoading(false);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    };
    getAccessToken();
    const interval = setInterval(getAccessToken, 50 * 60 * 1000); // 50 minutes

    const startKeepAwake = async () => {
      try {
        // Enable keep awake when recording starts
        await KeepAwake.keepAwake();

        // ... existing recording start code ...
      } catch (error) {
        console.error("Error starting keep awake:", error);
      }
    };

    const stopKeepAwake = async () => {
      try {
        await KeepAwake.allowSleep();
      } catch (error) {
        console.error("Error stopping keep awake:", error);
      }
    };

    startKeepAwake();

    return () => {
      clearInterval(interval);
      stopKeepAwake();
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/home");
    } else {
      navigate("/");
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <SentryErrorBoundary>
      <div className="app">
        <Routes>
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<PasswordReset />} />
          <Route
            path="/"
            element={<LoginPage setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute
                element={<HomePage />}
                isAuthenticated={isAuthenticated}
              />
            }
          />
        </Routes>
      </div>
    </SentryErrorBoundary>
  );
}
